import {useRef, useState, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {useSwipeable} from 'react-swipeable'

import useMediaQuery from '@mui/material/useMediaQuery'

import {useNavigate} from 'react-router-dom'

import {useWindowSize} from '../../../hooks'

import {getTopAddress, getBottomAddress} from '../../../utils'

import withSwipableImages from '../../hocs/withSwipableImages'

import {Image} from '../../generics'

import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import {Swiper, SwiperSlide} from 'swiper/react'
import {FreeMode} from 'swiper/modules'
import 'swiper/scss'
import 'swiper/css/free-mode'



const styles = {
  root: css`
    position: relative;
    display: flex;
    flex-direction: column;
  `,
  outerAddressContainer: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    @media (max-width: 1120px) { padding: 0 25px; }
  `,
  addressContainerWithImage: css`
    display: flex;
    align-items: center;
    gap: 20px;
    cursor: pointer;

    img {
      width: 72px;
      height: 72px;
      border-radius: 8px;
    }
  `,
  addressContainer: css`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 0px;

    h3 {
      font-weight: 500;
      font-size: 1rem;
    }

    p {
      color: #ccc;
    }
  `,
  mapLink: css`
    background-color: #262626;
    text-decoration: none;
    color: #fff;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 30px;
    font-size: 0.9rem;
    border-radius: 18px;
  `,
  addressLink: css`
    cursor: pointer;
  `,
  imagesContainer: css`
    margin: 30px 0 10px;
    max-height: 150px;
    & > div { padding-right: 150px; }
    @media (max-width: 1120px) { & > div { overflow: visible; padding: 0 100px 0 25px; } }
  `,
  image: css`
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    border-radius: 5px;
    object-fit: cover;
    object-position: center;
    cursor: pointer;
  `,
  modalContainer: css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 100vw;
    max-height: 100vh;
    overflow: hidden;
  `,
  modalImage: css`
    object-fit: contain;
    object-position: center;
    max-width: 90vw;
    max-height: 80vh;
  `,
  navButton: css`
    position: fixed;
    background-color: #fff !important;
    /* common rules… */
    @media (max-width: 768px) {
      /* mobile overrides… */
    }
  `,
  leftNav: css`
    left: 10px;
  `,
  rightNav: css`
    right: 10px;
  `,
  directions: css`
    display: none;
    @media (max-width: 768px) {
      display: block;
      position: fixed;
      bottom: 36px;
      color: #fff;
    }
  `
}

function InjectStyles() {
  useEffect(() => {
    const styleTag = document.createElement('style')
    styleTag.innerHTML = `
      .address-image-slide { width: 120px; height: 120px; position: relative; }
      .address-image-slide::before { content: ""; display: block; padding-top: 100%; }
      @media (max-width: 600px) { .address-image-slide { width: 100px; height: 100px; } }
    `
    document.head.appendChild(styleTag)
    return () => document.head.removeChild(styleTag)
  }, [])
  return null
}


const EventAddress = ({
  venue,
  images = [],
  index,
  setRef,
  address,
  location,
  setIndex,
  nextImage,
  showImage,
  previousImage
}) => {
  const ref = useRef()
  const navigate = useNavigate()
  const windowSize = useWindowSize()

  const [width, setWidth] = useState('100vw')

  const isSm = useMediaQuery(theme => theme.breakpoints.down('sm'))

  const venueImage = venue?.attributes?.imageDerivatives?.small ||
                      venue?.attributes?.image ||
                      venue?.attributes?.externalImage

  useEffect(() => {
    if (ref.current) {
      const { left } = ref.current.getBoundingClientRect()
      setWidth(window.innerWidth - left)
    }
  }, [windowSize.width])


  const swipeHandlers = useSwipeable({ 
    onSwipedDown: () => setIndex(null),
    delta: 50, 
    preventScrollOnSwipe: true 
  })

  const handleNavigate = () => navigate(`/venues/${venue.id}`)


  function getMapsUrl(address, venueName) {
    const parts = []

    if (venueName) {
      parts.push(venueName)
    }
    if (address.number && address.street) {
      parts.push(address.number + ' ' + address.street)
    }
    if (address.neighborhood) {
      parts.push(address.neighborhood)
    }
    if (address.city) {
      parts.push(address.city)
    }
    if (address.state) {
      parts.push(address.state)
    }
    if (address.postal) {
      parts.push(address.postal)
    }
    if (address.country) {
      parts.push(address.country)
    }

    const q = encodeURIComponent(parts.join(', '))
    return 'https://www.google.com/maps/search/?api=1&query=' + q
  }



  // When no venue, render simple detail section
  if (!venue) {
    return (
      <div css={styles.outerAddressContainer} style={{marginBottom: '25px'}}>    
        <div css={styles.addressContainer}>
          <h3>{location}</h3>
          <p>{getTopAddress(address)}</p>
          <p>{getBottomAddress(address)}</p>
        </div>

        <a
          css={styles.mapLink}
          href={getMapsUrl(address, location)}
          target='_blank'
          rel='noopener noreferrer'
          title='Open in Maps'
        >
          Open in Maps
        </a>
      </div>
    )
  }

  // Venue present: render address & images
  return (
    <>
      <InjectStyles />
      <div ref={ref} css={styles.root}>
        <div css={styles.outerAddressContainer}> 
          <div 
            css={styles.addressContainerWithImage} 
            style={{marginBottom: venueImage ? '10px' : 0}}
            onClick={handleNavigate}
          >
            {venueImage &&
              <Image src={venueImage} />
            }
            
            <div css={[styles.addressContainer, styles.addressLink]}>
              <div>
                <h3>{location}</h3>
                <p>{getTopAddress(address)}</p>
                <p>{getBottomAddress(address)}</p>
              </div>
            </div>
          </div>

           <a
            css={styles.mapLink}
            href={getMapsUrl(address, location)}
            target='_blank'
            rel='noopener noreferrer'
            title='Open in Maps'
          >
            Open in Maps
          </a>
        </div>

        {images.length > 0 && (
          <div css={styles.imagesContainer} style={{width}}>
            <Swiper spaceBetween={20} slidesPerView='auto' freeMode modules={[FreeMode]}>
              {images.map(img => (
                <SwiperSlide key={img.id} className='address-image-slide'>
                  <img
                    src={img.attributes.imageDerivatives?.small || img.attributes.image}
                    css={styles.image}
                    loading='lazy'
                    alt=''
                    onClick={showImage(img)}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <Dialog
          open={index !== null}
          onClose={() => setIndex(null)}
          maxWidth='md'
          PaperProps={{style: {backgroundColor: 'transparent'}}}
          BackdropProps={{style: {backgroundColor: 'rgba(0,0,0,0.95)'}}}
        >
          <div css={styles.modalContainer} {...swipeHandlers}>
            <IconButton onClick={previousImage} css={[styles.navButton, styles.leftNav]} size={isSm ? 'small' : 'medium'}>
              <ChevronLeftIcon />
            </IconButton>
            <img ref={setRef} src={images[index]?.attributes.image} css={styles.modalImage} alt='' />
            <IconButton onClick={nextImage} css={[styles.navButton, styles.rightNav]} size={isSm ? 'small' : 'medium'}>
              <ChevronRightIcon />
            </IconButton>
            <div css={styles.directions}><p>Swipe or click</p></div>
          </div>
        </Dialog>
      </div>
    </>
  )
}

export default withSwipableImages(EventAddress)






